// A cheap stand-in for the physics world for effect particles: every body's
// shapes cached in JS on a coarse grid, so a particle's step can be tested
// without a Box2D query per particle. Read-only, so it never touches the simulation.
const CELL = 2;
const RESCAN_FRAMES = 20;
const OFFSET = 32768;
// Bodies bigger than this many cells are tested by every query instead of
// filling the grid: a level's terrain can be hundreds of metres across.
const LARGE_CELLS = 64;
// A step that starts deeper than this inside a static body is buried in it.
// Shallower starts still get out, like a wound resting on the ground.
const BURIED = 0.05;

const entries = new Map();
const large = new Set();
let staticGrid = new Map();
let dynamicGrid = new Map();
let world = null;
let frame = 0;
let lastScan = -Infinity;
let query = 0;
let buried = null;

const keyOf = (cx, cy) => (cx + OFFSET) * 65536 + (cy + OFFSET);

const shapesOf = body => {
	const shapes = [];
	const minMax = {minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity};
	const grow = (x, y, r = 0) => {
		minMax.minX = Math.min(minMax.minX, x - r); minMax.minY = Math.min(minMax.minY, y - r);
		minMax.maxX = Math.max(minMax.maxX, x + r); minMax.maxY = Math.max(minMax.maxY, y + r);
	};
	for (let fixture = body.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) {
		if (fixture.IsSensor()) continue;
		const shape = fixture.GetShape();
		if (shape.GetType() === Box2D.b2Shape.e_circle) {
			const circle = Box2D.castObject(shape, Box2D.b2CircleShape);
			const p = circle.get_m_p();
			const s = {circle: true, x: p.get_x(), y: p.get_y(), r: circle.get_m_radius()};
			Object.assign(s, {minX: s.x - s.r, minY: s.y - s.r, maxX: s.x + s.r, maxY: s.y + s.r});
			shapes.push(s);
			grow(s.x, s.y, s.r);
		} else if (shape.GetType() === Box2D.b2Shape.e_polygon) {
			const polygon = Box2D.castObject(shape, Box2D.b2PolygonShape);
			const count = polygon.get_m_count();
			const xs = new Float64Array(count);
			const ys = new Float64Array(count);
			let cx = 0, cy = 0;
			for (let i = 0; i < count; i++) {
				const v = polygon.get_m_vertices(i);
				xs[i] = v.get_x();
				ys[i] = v.get_y();
				cx += xs[i] / count;
				cy += ys[i] / count;
				grow(xs[i], ys[i]);
			}
			// Outward edge normals, whichever way the polygon winds.
			const nx = new Float64Array(count);
			const ny = new Float64Array(count);
			const d = new Float64Array(count);
			for (let i = 0; i < count; i++) {
				const j = (i + 1) % count;
				let ex = ys[j] - ys[i];
				let ey = xs[i] - xs[j];
				const length = Math.sqrt(ex * ex + ey * ey) || 1;
				ex /= length;
				ey /= length;
				if ((cx - xs[i]) * ex + (cy - ys[i]) * ey > 0) {
					ex = -ex;
					ey = -ey;
				}
				nx[i] = ex;
				ny[i] = ey;
				d[i] = xs[i] * ex + ys[i] * ey;
			}
			shapes.push({circle: false, count, xs, ys, nx, ny, d,
				minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys)});
		}
	}
	return shapes.length ? {shapes, bounds: minMax} : null;
};

const insert = (grid, entry, keys) => {
	const {minX, minY, maxX, maxY} = entry.world;
	const x0 = Math.floor(minX / CELL), x1 = Math.floor(maxX / CELL);
	const y0 = Math.floor(minY / CELL), y1 = Math.floor(maxY / CELL);
	for (let cx = x0; cx <= x1; cx++) {
		for (let cy = y0; cy <= y1; cy++) {
			const key = keyOf(cx, cy);
			let list = grid.get(key);
			if (!list) grid.set(key, list = []);
			list.push(entry);
			keys?.push(key);
		}
	}
};

const cellCount = ({minX, minY, maxX, maxY}) =>
	(Math.floor(maxX / CELL) - Math.floor(minX / CELL) + 1) * (Math.floor(maxY / CELL) - Math.floor(minY / CELL) + 1);

// Takes an entry out of the static grid and the large set, wherever it is.
const unbucket = entry => {
	if (entry.cells) {
		for (const key of entry.cells) {
			const list = staticGrid.get(key);
			list[list.indexOf(entry)] = list[list.length - 1];
			list.pop();
			if (!list.length) staticGrid.delete(key);
		}
		entry.cells = null;
	}
	large.delete(entry);
};

// Reads the body's transform and its bounds in the world; returns whether it moved.
const place = entry => {
	const body = entry.body;
	const p = body.GetPosition();
	const x = p.x;
	const y = p.y;
	const angle = body.GetAngle();
	if (x === entry.x && y === entry.y && angle === entry.angle) return false;
	entry.x = x;
	entry.y = y;
	entry.angle = angle;
	entry.c = Math.cos(angle);
	entry.s = Math.sin(angle);
	const {minX, minY, maxX, maxY} = entry.bounds;
	const c = entry.c;
	const s = entry.s;
	const midX = (minX + maxX) / 2;
	const midY = (minY + maxY) / 2;
	const halfX = (maxX - minX) / 2;
	const halfY = (maxY - minY) / 2;
	const cx = x + c * midX - s * midY;
	const cy = y + s * midX + c * midY;
	const ex = Math.abs(c) * halfX + Math.abs(s) * halfY;
	const ey = Math.abs(s) * halfX + Math.abs(c) * halfY;
	const w = entry.world || (entry.world = {minX: 0, minY: 0, maxX: 0, maxY: 0});
	w.minX = cx - ex; w.maxX = cx + ex;
	w.minY = cy - ey; w.maxY = cy + ey;
	return true;
};

const rescan = accept => {
	const seen = new Set();
	for (let body = world.GetBodyList(); Box2D.getPointer(body) !== Box2D.getPointer(Box2D.NULL); body = body.GetNext()) {
		const pointer = Box2D.getPointer(body);
		if (body.destroyed || !accept(body)) continue;
		seen.add(pointer);
		const fixtures = Box2D.getPointer(body.GetFixtureList());
		let entry = entries.get(pointer);
		if (entry && entry.body === body && entry.fixtures === fixtures) continue;
		if (entry) unbucket(entry);
		const cached = shapesOf(body);
		if (!cached) {
			entries.delete(pointer);
			continue;
		}
		entry = {body, fixtures, ...cached, isStatic: false, cells: null, x: NaN, y: NaN, angle: NaN, c: 1, s: 0, world: null, query: 0};
		entries.set(pointer, entry);
	}
	for (const [pointer, entry] of entries) {
		if (!seen.has(pointer)) {
			unbucket(entry);
			entries.delete(pointer);
		}
	}
};

const clearIndex = () => {
	entries.clear();
	large.clear();
	staticGrid = new Map();
	dynamicGrid = new Map();
};

// Once per frame. `accept(body)` says which bodies effects can hit.
export const refreshField = (physicsWorld, accept) => {
	frame++;
	if (physicsWorld !== world) {
		world = physicsWorld;
		clearIndex();
		lastScan = -Infinity;
	}
	if (!world) return;
	if (frame - lastScan >= RESCAN_FRAMES) {
		rescan(accept);
		lastScan = frame;
	}
	dynamicGrid = new Map();
	for (const [pointer, entry] of entries) {
		if (entry.body.destroyed) {
			unbucket(entry);
			entries.delete(pointer);
			continue;
		}
		// Triggers can make a static body dynamic, or move it.
		const isStatic = entry.body.GetType() === Box2D.b2_staticBody;
		if (place(entry) || isStatic !== entry.isStatic) {
			unbucket(entry);
			entry.isStatic = isStatic;
			if (cellCount(entry.world) > LARGE_CELLS) large.add(entry);
			else if (isStatic) insert(staticGrid, entry, entry.cells = []);
		}
		if (!entry.isStatic && !large.has(entry)) insert(dynamicGrid, entry);
	}
};

export const clearField = () => {
	clearIndex();
	world = null;
};

// Where a segment from outside a shape first enters it, in local space: t in [0, 1]
// and the edge normal. Returns 1 for a hit, -1 for a start buried in the shape, else 0.
const enterPolygon = (shape, ax, ay, bx, by, out) => {
	let tEnter = 0;
	let tLeave = 1;
	let normal = -1;
	let depth = Infinity;
	const dx = bx - ax;
	const dy = by - ay;
	for (let i = 0; i < shape.count; i++) {
		const num = shape.d[i] - (shape.nx[i] * ax + shape.ny[i] * ay);
		const den = shape.nx[i] * dx + shape.ny[i] * dy;
		if (num < depth) depth = num;
		if (den === 0) {
			if (num < 0) return 0;
			continue;
		}
		const t = num / den;
		if (den < 0) {
			if (t > tEnter) {
				tEnter = t;
				normal = i;
			}
		} else if (t < tLeave) {
			tLeave = t;
		}
		if (tEnter > tLeave) return 0;
	}
	// Without an edge to come in through, the start is inside, depth from the nearest edge.
	if (normal < 0) return depth > BURIED ? -1 : 0;
	out.t = tEnter;
	out.nx = shape.nx[normal];
	out.ny = shape.ny[normal];
	return 1;
};

const enterCircle = (shape, ax, ay, bx, by, out) => {
	const fx = ax - shape.x;
	const fy = ay - shape.y;
	const c = fx * fx + fy * fy - shape.r * shape.r;
	if (c <= 0) return shape.r - Math.sqrt(fx * fx + fy * fy) > BURIED ? -1 : 0;
	const dx = bx - ax;
	const dy = by - ay;
	const a = dx * dx + dy * dy;
	const b = fx * dx + fy * dy;
	const disc = b * b - a * c;
	if (disc < 0 || a === 0) return 0;
	const t = (-b - Math.sqrt(disc)) / a;
	if (t < 0 || t > 1) return 0;
	const hx = fx + dx * t;
	const hy = fy + dy * t;
	out.t = t;
	out.nx = hx / shape.r;
	out.ny = hy / shape.r;
	return 1;
};

const scratch = {t: 0, nx: 0, ny: 0};
const result = {body: null, t: 0, x: 0, y: 0, nx: 0, ny: 0, buried: false};

const testEntry = (entry, ax, ay, bx, by, best) => {
	if (entry.query === query) return best;
	entry.query = query;
	const w = entry.world;
	if (Math.max(ax, bx) < w.minX || Math.min(ax, bx) > w.maxX || Math.max(ay, by) < w.minY || Math.min(ay, by) > w.maxY) return best;
	const {c, s} = entry;
	const lax = c * (ax - entry.x) + s * (ay - entry.y);
	const lay = -s * (ax - entry.x) + c * (ay - entry.y);
	const lbx = c * (bx - entry.x) + s * (by - entry.y);
	const lby = -s * (bx - entry.x) + c * (by - entry.y);
	const minX = Math.min(lax, lbx), maxX = Math.max(lax, lbx);
	const minY = Math.min(lay, lby), maxY = Math.max(lay, lby);
	for (const shape of entry.shapes) {
		if (maxX < shape.minX || minX > shape.maxX || maxY < shape.minY || minY > shape.maxY) continue;
		const hit = shape.circle ? enterCircle(shape, lax, lay, lbx, lby, scratch) : enterPolygon(shape, lax, lay, lbx, lby, scratch);
		if (hit < 0 && entry.isStatic) buried = buried || entry.body;
		if (hit <= 0 || scratch.t >= best) continue;
		best = scratch.t;
		result.body = entry.body;
		result.nx = c * scratch.nx - s * scratch.ny;
		result.ny = s * scratch.nx + c * scratch.ny;
	}
	return best;
};

const testCell = (grid, key, ax, ay, bx, by, best) => {
	const list = grid.get(key);
	if (!list) return best;
	for (const entry of list) best = testEntry(entry, ax, ay, bx, by, best);
	return best;
};

// The first body a segment (in metres) runs into from outside it, or null. A
// segment that starts buried in a static body hits it at its start, with
// `buried` set and no normal. The result object is reused: copy what you need
// before the next call.
export const segmentHit = (ax, ay, bx, by) => {
	if (!entries.size) return null;
	query++;
	buried = null;
	let best = Infinity;
	const start = keyOf(Math.floor(ax / CELL), Math.floor(ay / CELL));
	const end = keyOf(Math.floor(bx / CELL), Math.floor(by / CELL));
	best = testCell(staticGrid, start, ax, ay, bx, by, best);
	best = testCell(dynamicGrid, start, ax, ay, bx, by, best);
	if (end !== start) {
		best = testCell(staticGrid, end, ax, ay, bx, by, best);
		best = testCell(dynamicGrid, end, ax, ay, bx, by, best);
	}
	for (const entry of large) best = testEntry(entry, ax, ay, bx, by, best);
	if (buried) {
		result.body = buried;
		result.nx = result.ny = 0;
		best = 0;
	} else if (best === Infinity) {
		return null;
	}
	result.buried = !!buried;
	result.t = best;
	result.x = ax + (bx - ax) * best;
	result.y = ay + (by - ay) * best;
	return result;
};

export const fieldSize = () => entries.size;
